import { type UserSession } from '@solinkcorp/authentication';
import { type ParsedPlaylist, type VideoUrlProps } from '@solinkcorp/playlist-parser';
import { getAccessibleAddress } from '@solinkcorp/utils';
import { type AuthLocation } from '@solinkcorp/vd-common/lib/esm/auth/authUtils';
import { type PlayingCamera } from 'components/VideoPlayer/controller/PlayingCamerasController';
import type VideoWallAnalyticsCache from 'components/VideoWallPage/videoWallAnalyticsCache';
import { castDraft, type Draft, type Immutable } from 'immer';
import moment from 'moment-timezone';
import { ImmutableModelStore } from 'solox';
import { type AnalyticsPlatform } from 'src/core/analytics/common';
import { getErrorMessage } from 'src/core/utils/errorUtils';
import { type SelectedStream, type StreamQuality } from 'src/core/video/types';
import * as videoAnalytics from 'src/core/video/videoAnalyticsLogger';
import * as VideoUtil from 'src/core/video/VideoUtil';
import type { SetOptional } from 'type-fest';
import { type PlaylistCache } from 'utils/PlaylistCache/PlaylistCache';
import { findClosestSegmentIndex, isInSegment } from 'utils/PlaylistCache/SegmentUtils';
import { type SelectedPlayListSource } from 'utils/types';
import { type ThumbnailLevel } from '../components/PlayerThumbnail/VideoThumbnail';
import { MAX_ALLOWED_SEGMENT_GAP } from '../components/VideoHistogram/common/RecordingBars';
import { HighResTimestamp } from '../utils/HighResTimestamp';
import { PressureStateTracker } from '../utils/PressureStateTracker';
import { CircularLog } from './CircularLog';
import { FFWD_AND_REWIND_TICK_INTERVAL } from './usePlaybackControllerTick';

const PC = '[PlaybackController]';

const HLS_DEBUG = 'HLS_DEBUG';
/** Time in seconds (approximately) until we restart a player after an error. */
export const PLAYER_ERROR_CHECKING_HEART_BEAT = 20;

// Gap of 4 seconds between live and archive video, this is set to 4 seconds as this is the minimal length of a fragmented segment.
export const LIVE_MODE_BARRIER_OFFSET = 4 * 1000;
const MIN_TIME_BETWEEN_PLAYLIST_RELOADS = 30000;
const MIN_TIME_BETWEEN_CORRECTIONS = 10000;
const CLOSE_TO_LIVE_OFFSET = 1 * 60 * 1000;

/** How many log entries to keep, per-player. */
const DEFAULT_MAX_LOG_ENTRIES = 500;

/** If a player is seeking for longer than this, we'll reset the player. */
const MAX_SEEK_TIME = 20000;

/**
 * In practice, the player may report that it has reached the end of the playlist
 * a few seconds before the actual end of the playlist.  If this happens,
 * we treat it as ended.  (TODO: It seems like video ends when we still have one
 * segment left - is this maybe a bug in hls.js?)
 */
const ENDED_TOLERANCE_MS = 60000;

/**
 * If we're playing within this many ms of the end of the current playlist, and
 * we're not close to playing live, then we'll pre-fetch the playlist after the
 * current one.
 */
const PREFETCH_THRESHOLD = 10 * 60 * 1000; // 10 minutes

// If we don't know or can't determine the frame rate of a player, we'll default to
// this frame rate.
const DEFAULT_FPS = 2;

/**
 * "Short range" span.  When we load video for a player, if we have no cached
 * data, we'll grab a "short" playlist for the time immediately surrounding
 * the current play time, to try to get video playing as quickly as possible.
 * When fetching a short playlist, we'll fetch this amount on either side of the
 * current play time.
 */
export const SHORT_RANGE_SPAN_MS = 15 * 60 * 1000; // 15 minutes

/**
 * A player must be within this many ms of the desired time, or we will
 * seek that player.
 */
export const OUT_OF_SYNC_TOLERANCE = 2000;

export type PlaybackState = 'paused' | 'playing' | 'live';

export type LoadedSegments = {
  readonly state: 'loaded';
  /** True if this is a short playlist. */
  readonly short: boolean;
  /** The playing quality for this segment. */
  readonly playingQuality: StreamQuality;
} & Immutable<VideoUtil.SegmentData>;

/** Represents loaded/loading/errored segments for a player. */
export type PlayerSegments =
  | { readonly state: 'loading' }
  | { readonly state: 'error'; readonly error: Error; readonly timestamp: number }
  | LoadedSegments;

/** BufferedSegment represents a segment that is buffered by the browser. */
export interface BufferedSegment {
  readonly start: number;
  readonly end: number;
}

/** StreamInfo represents information about a selected stream, and where we're loading that stream from. */
export interface StreamInfo {
  readonly streamId: string;
  readonly source: 'local' | 'cloud';
  readonly quality: StreamQuality;
}

/** PlaybackGap represents a gap in a playlist. */
export interface PlaybackGap {
  start: number;
  end: number;
  /** If this is true, then we don't know the exact end of this gap, so it should not be skipped. */
  indefinite?: boolean;
}

/**
 * Information we store about each player.
 */
interface PerPlayerState {
  /** The desired state of the player, and metrics about the player, generated by the PlaybackController. */
  readonly state: PlayerState;
  /** The actual status of the player, reported by the player. */
  readonly status?: PlayerStatus;
}

export interface PlaybackControllerState {
  /** The current playback quality. */
  readonly playingQuality: StreamQuality;
  /** The time at which this playbackController was last updated. */
  readonly lastUpdated: number;
  /** The current playback state. */
  readonly playbackState: PlaybackState;
  /** The desired playback time, in wall-clock time. */
  readonly desiredTime: number;
  /** Seeking past this time will result in us playing live video. */
  readonly liveModeBarrier: number;
  /**
   * If we are playing live, this will be set to the time we were last
   * playing archive.  This is the time we'll seek to if we toggle live off.
   */
  readonly lastArchiveTime: number | undefined;
  /**
   * The current playback speed - 1x, 2x, etc...
   * This value is ignored when playing live.
   */
  readonly playbackRate: number;
  /**
   * If this is true, the playback controller will skip any gaps in video.
   * This is set false when the user seeks or when we first load video.  The idea
   * here is if the user intentionally seeks into a gap, or if the user clicks
   * on an event that starts in a gap, we want to show the player at the time
   * the user picked instead of immediately jumping to some other time.  If
   * we're just playing along and all players have video, this will get set to true.
   */
  readonly skipGaps: boolean;
  /** State for each currently playing camera, indexed by playerId. */
  readonly byPlayerId: {
    [playerId: string]: PerPlayerState | undefined;
  };
}

/**
 * The state for an individual player.
 */
export interface PlayerState {
  /** The stream this player should be playing. */
  readonly streamInfo: StreamInfo | undefined;
  /** The player we want to use to play this content. */
  readonly playerType: VideoUtil.VideoPlayerType | undefined;
  /** The playlist, m3u8 file, time translation function, etc... that the player is currently playing. */
  readonly playlist: PlayerSegments | undefined;
  readonly liveplaylist: PlayerSegments | undefined;
  /**
   * When we first load a player, we only load playlist info for 2 hours
   * around the desiredTime.  If the user tries to seek outside this, we want
   * the whole 24 hour playlist available immediately, so we load it ahead
   * of time.  Similarly, if the user is playing close to the end of the day,
   * we'll pre-fetch the next day's playlist so we can transition to the
   * next day when we reach the end of the current playlist.
   */
  readonly nextPlaylist: PlayerSegments | undefined;
  /** True if the device is offline, false if online, undefined if unknown. */
  readonly deviceOffline: boolean | undefined;
  /** If true, the player should re-initialize itself. */
  readonly restartRequired: boolean;
  /**
   * Set if the player needs to seek to a new time.
   */
  readonly seekRequired:
    | undefined
    | {
        /** The time to seek to. */
        seekTo: number;
        /** The time we started this seek. */
        requestedAt: number;
      };
  /**
   * This will be set when the player has no video for the current desiredTime.
   * In this case, `inGap` will be set to the {start, end} of the region we
   * don't have video for, in wall-clock time.  If this is set, the player
   * should pause and display a "no video until..." message.
   */
  readonly inGap: PlaybackGap | undefined;
  /** A count of the number of times we've had to resync this player. */
  readonly corrections: number;
  /** The time of the most recent correction. */
  readonly lastCorrectionTimestamp?: number;
  /** Number of times we've forced this player to restart. */
  readonly restarts: number;
  /** A count of the number of times this player has been waiting for video. */
  readonly waitingCount: number;
  /** This is updated every time the `state` changes in the player's status. */
  readonly lastStateChange: HighResTimestamp;
  /** The time the player first started loading video. */
  readonly loadingStart: HighResTimestamp | undefined;
  /** The time the player first started playing video. */
  readonly playingStart: HighResTimestamp | undefined;
  /** The last time the player was paused. */
  readonly pausingStart: HighResTimestamp | undefined;
  /** The total time the player has been paused. */
  readonly totalPausedTime: number;
  /** An array of error generated during playback */
  readonly errors: Readonly<string[]>;
}

/**
 * PlayerStatus is a data structure passed from the player up to the PlaybackController
 * to report on the player's status.
 */
export interface PlayerStatus {
  /** The time this status was created. */
  readonly timestamp: HighResTimestamp;
  readonly type: 'Archived' | 'Live';
  /**
   * Free-form string with information about the player - hls.js, connect-hls,
   * is the player low-latency, etc...
   */
  readonly playerInfo?: string;
  /** The current state of this player. */
  readonly state: 'loading' | 'playing' | 'waiting' | 'paused' | 'ended' | 'error';
  /**
   * Most recent reported playback time for the player.  This will be undefined
   * if the player is loading or has ended.
   */
  readonly wallclockTime?: number;
  /**
   * The framerate for this player, if known - if we try to pause the player before
   * we've calculated this, we might let the player play on for a second or
   * two with a "Calculating frame rate..." message overtop of the video,
   * then seek it back to the paused time when we're done.
   */
  readonly frameRate?: number;
  /**
   * When the player seeks to an unbuffered time, this it how long the seek took.
   * This is used by PlaybackController to estimate how long a seek operation will
   * take.
   */
  readonly lastUnbufferedSeekDuration?: number;
  /**
   * Loaded buffers from the `<video>` element.  Note that start and end here
   * are wall clock time.
   */
  readonly buffered?: readonly BufferedSegment[];
  /** Total buffered video loaded by the player, measured in seconds. */
  readonly totalBufferedSeconds?: number;
  /**
   * If the PlaybackController sets `seekRequired` on a player, the player must
   * set this in a subsequent status update to acknowledge the seek was completed.
   */
  readonly seekAcknowledged?: number;
  /**
   * If the PlaybackController sets `restartRequired`, the player must set this
   * in a subsequent status update.
   */
  readonly restartAcknowledged?: boolean;
  /** Set if this player has encountered an error and can't play video. */
  readonly error?: unknown;
}

/**
 * PlaybackController is a controller used to coordinate one or more video players.
 *
 * Each archive player should do the following:
 *
 * - At least once a second, or whenever the player status changes, players
 *   should call `updatePlayerStatus()`.
 * - Whenever the `playlist` for the player changes, the archive player should
 *   load video and seek to the `desiredTime`.
 * - Whenever `seekRequired` is set, the player should seek to the specified
 *   time and then call the `playerSeekCallback` action.
 * - The desired time has different meanings depending on whether segmentDurationMs is provided:
 *   - If segmentDurationMs is provided: desiredTime is the START time of the segment (Video Search)
 *   - If segmentDurationMs is NOT provided: desiredTime is the CENTER time around which we load video
 *
 */
export class PlaybackController {
  private user: UserSession | undefined;
  private playlistCache: PlaylistCache;
  private playingCameras: readonly PlayingCamera[] = [];
  private localLocations: Record<string, boolean | undefined>;
  private playingCamerasByPlayerId: { [playerId: string]: PlayingCamera } = {};
  private selectedStreamByPlayerId: { [playerId: string]: SelectedStream | undefined } = {};
  private playListSource: SelectedPlayListSource;
  private vtt: boolean;
  private quick: boolean;
  private segmentDurationMs?: number;
  private preferredVideoPlayerOverride: {
    [playerId: string]: VideoUtil.VideoPlayerType | undefined;
  } = {};
  /** playerType override when any players fail, to be used as fallback player type */
  private playerTypeFallback: {
    [playerId: string]: VideoUtil.VideoPlayerType | undefined;
  } = {};
  private _checkDeviceOnline: ((location: AuthLocation) => Promise<boolean>) | undefined;
  public state: ImmutableModelStore<PlaybackControllerState>;
  public playerLogs: { [playerId: string]: CircularLog } = {};
  private videoPlaybackStartsAtDate: Date | undefined;
  private videoPlaybackEndsAtDate: Date | undefined;
  private disableLiveModeBarrier: boolean;
  private thumbnailLevel?: ThumbnailLevel;
  private videoPlayerPlatform?: AnalyticsPlatform;
  private videoWallAnalyticsCache?: VideoWallAnalyticsCache;
  private pressureStateTracker: PressureStateTracker;
  private timezone: string;

  /**
   * Create a new PlaybackController.
   * Note that in most cases you'll want to immediately call `setPlayingCameras()`.
   *
   * @param user - The current user.
   * @param playlistCache - PlaylistCache to fetch playlists from.
   * @param checkDeviceOnline - Function to call to check if a device is online.
   * @param playlistSource - The current playlist source for the current user.
   *   If this changes, it should be updated via `updatePlaylistSource()`.
   * @param initialDesiredTime - The time the PlaybackController should start
   *   playing at.
   * @param initialPlayingQuality - True if we should start out playing HD.
   * @param initialPlayingCameras - The initial set of playing cameras.  If this
   *   changes, it should be updated via `setPlayingCameras()`.
   * @param [options.quick=true] - If true, then this PlaybackController is being
   *   used to control a "quick player". PlaybackController will preferentially
   *   choose players that have faster startup time.
   * @param [options.preferredVideoPlayer] - The preferred video player.  If this
   *   is set, we'll use this video player whenever we can instead of the default
   *   video player.
   * @param [options.segmentDurationMs] - The duration of the segment to load.
   *   If this is provided, we will load a specific duration of video starting from desiredTime.
   *   If not provided, we will load a longer range of video centered around desiredTime.
   */
  constructor(
    user: UserSession | undefined,
    playlistCache: PlaylistCache,
    // getRecordingSegmentsFromM3U8 checks if device is online
    // we can pass in a funtion that returns true by default for the public pages
    checkDeviceOnline: ((location: AuthLocation) => Promise<boolean>) | undefined,
    playListSource: SelectedPlayListSource,
    initialDesiredTime: number,
    private initialPlayingQuality: StreamQuality,
    initialPlayingCameras: PlayingCamera[],
    localLocations: Record<string, boolean | undefined>,
    options: {
      vtt?: boolean;
      quick?: boolean;
      disableLiveModeBarrier?: boolean;
      thumbnailLevel?: ThumbnailLevel;
      segmentDurationMs?: number;
    } = {},
    videoPlaybackStartsAtDate: Date | undefined = undefined,
    videoPlaybackEndsAtDate: Date | undefined = undefined,
    timezone: string,
    videoPlayerPlatform?: AnalyticsPlatform,
    videoWallAnalyticsCache?: VideoWallAnalyticsCache,
  ) {
    this.user = user;
    this.localLocations = localLocations;
    this.playlistCache = playlistCache;
    this.vtt = options.vtt ?? false;
    this.quick = options.quick ?? false;
    this.videoPlaybackStartsAtDate = videoPlaybackStartsAtDate;
    this.videoPlaybackEndsAtDate = videoPlaybackEndsAtDate;
    this.timezone = timezone;
    this.disableLiveModeBarrier = !!options.disableLiveModeBarrier;
    this.thumbnailLevel = options.thumbnailLevel;
    this.videoPlayerPlatform = videoPlayerPlatform;
    this.videoWallAnalyticsCache = videoWallAnalyticsCache;
    this.segmentDurationMs = options.segmentDurationMs;

    const now = Date.now();
    const liveModeBarrier = now - LIVE_MODE_BARRIER_OFFSET;

    this.playListSource = playListSource;
    this._checkDeviceOnline = checkDeviceOnline;

    let desiredTime = initialDesiredTime;
    if (typeof initialDesiredTime !== 'number' || isNaN(initialDesiredTime)) {
      console.error(
        `PlaybackController: initialDesiredTime is not a number: ${initialDesiredTime}`,
      );
      desiredTime = now;
    }

    this.state = new ImmutableModelStore<PlaybackControllerState>({
      playingQuality: initialPlayingQuality,
      playbackState:
        initialDesiredTime > liveModeBarrier && !this.disableLiveModeBarrier ? 'live' : 'playing',
      lastUpdated: now,
      desiredTime,
      liveModeBarrier,
      lastArchiveTime: undefined,
      playbackRate: 1,
      skipGaps: false,
      byPlayerId: {},
    });

    this.setPlayingCameras(initialPlayingCameras);
    this.pressureStateTracker = new PressureStateTracker('cpu');
  }

  destroyPressureStateTracker() {
    this.pressureStateTracker.destroy();
  }

  getState(): PlaybackControllerState {
    return this.state.current;
  }

  updateVideoPlaybackStartsAtDate = (videoPlaybackStartsAtDate: Date | undefined): void => {
    this.videoPlaybackStartsAtDate = videoPlaybackStartsAtDate;
  };

  updateVideoPlaybackEndsAtDate = (videoPlaybackEndsAtDate: Date | undefined): void => {
    this.videoPlaybackEndsAtDate = videoPlaybackEndsAtDate;
  };

  /**
   * Given a player time in wallclock time, this returns the start and end times
   * to load video segments for.  Note that this is overridden in unit tests!
   */
  getLoadedVideoDomain(time: number, timezone: string): [number, number] {
    const begin = moment.tz(time, timezone).startOf('day');
    const end = moment.tz(time, timezone).endOf('day');

    return [begin.valueOf(), end.valueOf()];
  }

  /**
   * Switch to playing live.
   */
  playLive = (): void => {
    this.state.update((state) => {
      state.lastArchiveTime = state.desiredTime;
      // Update the desired time.
      state.lastUpdated = state.desiredTime = Date.now();

      state.playbackState = 'live';
      for (const [playerId, playerState] of Object.entries(state.byPlayerId)) {
        this.getPlayerLog(playerId).info(`${PC} Switching to live`);
        if (playerState) {
          playerState.state.seekRequired = undefined;
          playerState.state.inGap = undefined;
          playerState.status = undefined;

          // If the playlist has a loading error, clear the playlist.  We can
          // retry immediately when we switch back from live.
          if (playerState.state.playlist?.state === 'error') {
            playerState.state.playlist = undefined;
            playerState.state.nextPlaylist = undefined;
          }
        }
      }

      this.setPlaybackRate(1);
      this._updatePlayers(state);

      // Reset the playing quality to the initial playing quality.
      // This is to ensure that if we were playing an ad stream, we do not try to play an AD stream live.
      if (state.playingQuality === 'ad') {
        state.playingQuality = this.initialPlayingQuality;
      }
    });
  };

  /**
   * Toggle to playing live if playing archive, or vice versa.
   */
  toggleLive = (playLive?: boolean) => {
    const oldPlayingLive = this.isPlayingLive();
    const newPlayingLive = playLive ?? !oldPlayingLive;

    if (oldPlayingLive === newPlayingLive) {
      return;
    }

    if (newPlayingLive) {
      this.playLive();
    } else {
      const newPlaybackTime = Math.min(
        this.state.current.lastArchiveTime ?? Date.now(),
        this.state.current.liveModeBarrier - 60000,
      );

      this.seekToTime(newPlaybackTime);
    }
  };

  /**
   * Set which locations are locally accessible.
   */
  setLocalLocations(localLocations: Record<string, boolean | undefined>) {
    this.localLocations = localLocations;
    this.state.update((state) => {
      this._updatePlayers(state);
    });
  }

  /**
   * Set the current play time to the specified wallclock time.
   */
  seekToTime(seekTime: number | ((currentTime: number) => number), rewind?: boolean): void {
    const { desiredTime, liveModeBarrier } = this.state.current;

    let newTime = typeof seekTime === 'function' ? seekTime(desiredTime) : seekTime;

    if (typeof newTime !== 'number' || isNaN(newTime)) {
      throw new Error(`Invalid seekTime ${newTime} of type ${typeof newTime}`);
    }

    if (
      this.videoPlaybackStartsAtDate &&
      VideoUtil.isSeekTimeBeforeVideoStartDate(newTime, this.videoPlaybackStartsAtDate)
    ) {
      newTime = this.videoPlaybackStartsAtDate.valueOf();
    }

    if (
      this.videoPlaybackEndsAtDate &&
      VideoUtil.isSeekTimeAfterVideoEndDate(newTime, this.videoPlaybackEndsAtDate)
    ) {
      newTime = this.videoPlaybackEndsAtDate.valueOf();
    }

    if (newTime >= liveModeBarrier && !this.disableLiveModeBarrier) {
      this.playLive();
    } else {
      // This should only happen if the live mode barrier is disabled.
      // Don't allow the user to seek past the live mode barrier.
      if (newTime >= liveModeBarrier) {
        newTime = liveModeBarrier;
      }

      this.state.update((state) => {
        state.lastUpdated = Date.now();

        if (this._isPlayingLive(state)) {
          state.playbackState = 'playing';
        }

        state.desiredTime = newTime;

        // We dont want to set skipGaps to false if the seek was initiated by rewinding
        if (!rewind) {
          state.skipGaps = false;
        }

        // Seek all players to the new time.
        for (const [playerId, playerState] of Object.entries(state.byPlayerId)) {
          // If there's status from the live player, clear it.
          if (playerState?.status?.type === 'Live') {
            playerState.status = undefined;
          }

          if (
            playerState?.state.playlist?.state === 'loaded' &&
            inLoadedRange(playerState.state.playlist, newTime)
          ) {
            const log = this.getPlayerLog(playerId);
            log.info(`${PC} Seeking to ${new Date(newTime).toISOString()}`);
            playerState.state.seekRequired = {
              seekTo: newTime,
              requestedAt: Date.now(),
            };
            // We dont want to set inGap to undefined if the seek was initiated by rewinding
            if (!rewind) {
              playerState.state.inGap = undefined;
            }
          } else {
            // Player needs a new playlist.  The player will seek to the
            // desiredTime when it gets one.
          }
        }
        this._updatePlayers(state);
      });
    }
  }

  /** Advance forward or backward one frame. */
  advanceFrame(frameCount: number): void {
    // Each player may have a different frame rate.  For example, if one player
    // is playing at 10 FPS, and another is playing at 5 FPS, then when we go
    // forward one frame we want to update the 10 FPS player every time, but
    // the 5 FPS player only every second time.
    //
    // Whenever we want to go forward one frame, we figure out which player has
    // the highest frame rate, and then we advance the desired time enough
    // to go forward one frame for that player.  Then, for each player, if
    // the player's current playing time is off by more than one frame, we
    // seek that player.

    // Work out the highest FPS of any playing camera.
    let maxFps: number | undefined;
    for (const state of Object.values(this.state.current.byPlayerId)) {
      const playerFps = state?.status?.frameRate;
      if (playerFps !== undefined && (maxFps === undefined || playerFps > maxFps)) {
        maxFps = playerFps;
      }
    }
    if (maxFps === undefined) {
      // Pick a reasonable default.
      maxFps = DEFAULT_FPS;
    }

    const frameDurationMs = 1000 / maxFps;

    // Seek any players that need seeking.
    this.state.update((state) => {
      const newDesiredTime = state.desiredTime + Math.ceil(frameCount * frameDurationMs);
      if (
        !this.videoPlaybackStartsAtDate ||
        !VideoUtil.isSeekTimeBeforeVideoStartDate(newDesiredTime, this.videoPlaybackStartsAtDate) ||
        !(
          this.videoPlaybackEndsAtDate &&
          VideoUtil.isSeekTimeAfterVideoEndDate(newDesiredTime, this.videoPlaybackEndsAtDate)
        )
      ) {
        state.desiredTime = newDesiredTime;
      }

      for (const [playerId, playerState] of Object.entries(state.byPlayerId)) {
        const log = this.getPlayerLog(playerId);
        const status = playerState?.status;
        const wallclockTime = status?.wallclockTime;

        if (wallclockTime && playerState?.state.playlist?.state === 'loaded') {
          const playerFps = status?.frameRate ?? DEFAULT_FPS;
          const deltaMs = Math.abs(state.desiredTime - wallclockTime);
          if (deltaMs >= 1000 / playerFps) {
            log.info(`${PC} Advancing frame to ${new Date(state.desiredTime).toISOString()}`);
            playerState.state.seekRequired = {
              seekTo: state.desiredTime,
              requestedAt: Date.now(),
            };
          }
        }
      }
    });
  }

  /** This must be called at least once a second to update the state of the PlaybackController. */
  tick = (): void => {
    this.state.update((state) => {
      const currentTime = Date.now();
      const elapsed = currentTime - state.lastUpdated;
      state.lastUpdated = currentTime;
      state.liveModeBarrier = currentTime - LIVE_MODE_BARRIER_OFFSET;

      switch (state.playbackState) {
        case 'live':
          state.desiredTime = currentTime;
          break;
        case 'paused':
          // Nothing to do...
          break;
        case 'playing': {
          // Rewinding
          if (state.playbackRate < 0) {
            const somePlayersSeeking = Object.values(state.byPlayerId).some((playerState) => {
              return playerState?.state.seekRequired;
            });

            if (!somePlayersSeeking) {
              this.seekToTime((playerTime) => {
                const desiredSeekTime =
                  playerTime + state.playbackRate * FFWD_AND_REWIND_TICK_INTERVAL;

                if (
                  this.videoPlaybackStartsAtDate &&
                  VideoUtil.isSeekTimeBeforeVideoStartDate(
                    desiredSeekTime,
                    this.videoPlaybackStartsAtDate,
                  )
                ) {
                  this.togglePaused(true, true);
                  return playerTime;
                }
                return desiredSeekTime;
              }, true);
            }
          }
          // Playing video normally
          else {
            const allPlayersInGap = Object.values(state.byPlayerId).every(
              (playerState) => !!playerState?.state.inGap,
            );

            // If all players are waiting for video, don't bother advancing time.
            const allPlayersWaiting =
              !allPlayersInGap &&
              Object.values(state.byPlayerId).every(
                (playerState) =>
                  playerState?.status?.state === 'waiting' && !playerState?.state.inGap,
              );

            // If no players have loaded data for the current desired time, don't
            // bother trying to advance time.
            const somePlayersHaveVideo =
              !allPlayersInGap &&
              Object.values(state.byPlayerId).some(
                (playerState) =>
                  playerState?.status &&
                  playerState.status.state !== 'loading' &&
                  inLoadedRange(playerState.state.playlist, state.desiredTime),
              );

            if (allPlayersInGap || (!allPlayersWaiting && somePlayersHaveVideo)) {
              state.desiredTime += Math.ceil(elapsed * state.playbackRate);
            }

            if (
              this.videoPlaybackEndsAtDate &&
              VideoUtil.isSeekTimeAfterVideoEndDate(state.desiredTime, this.videoPlaybackEndsAtDate)
            ) {
              state.desiredTime = this.videoPlaybackEndsAtDate.valueOf();
              this.togglePaused(true, true);
            }
          }

          break;
        }
        default:
          throw new Error(`Unknown state: ${state.playbackState}`);
      }

      // If the desiredTime gets past the liveModeBarrier (because we're
      // playing in FFWD) then switch to playing live.
      if (
        state.playbackState !== 'live' &&
        state.desiredTime > state.liveModeBarrier &&
        !this.disableLiveModeBarrier
      ) {
        this.playLive();
      } else {
        this._updatePlayers(state);
      }

      if (state.playbackState !== 'live') {
        // If all players are playing, set `skipGaps` to true so we'll automatically skip over gaps.
        if (!state.skipGaps && allPlayersNotInGap(state)) {
          state.skipGaps = true;
        } else {
          this._skipGaps(state);
        }
      }
    });
  };

  /** Update the current user. */
  setUser(user: UserSession): void {
    if (!this.user) {
      throw new Error("setUser can't be called before user is initialized");
    }

    const changed =
      this.user.accessToken !== user.accessToken || this.user.customerId !== user.customerId;
    this.user = user;

    // Force all streams to reload if token or tenantId has changed.
    if (changed) {
      this.state.update((state) => {
        this._updatePlayers(state, true);
      });
    }
  }

  /** Set the currently playing cameras. */
  setPlayingCameras(cameras: readonly PlayingCamera[]): void {
    if (cameras === this.playingCameras) {
      return;
    }

    this.playingCameras = cameras;
    this.playingCamerasByPlayerId = {};
    for (const camera of cameras) {
      this.playingCamerasByPlayerId[camera.playerId] = camera;
    }

    this.state.update((state) => {
      // Add any missing players...
      for (const { playerId, camera } of cameras) {
        if (state.byPlayerId[playerId]) {
          // Already have this player...
        } else {
          this.selectedStreamByPlayerId[playerId] = this._getStreamForPlayer(state, playerId);
          this.getPlayerLog(playerId).debug(`${PC} Added player ${camera.name}.`);
          const streamInfo = this._getStreamInfoForPlayer(state, playerId);
          const playerType = this._getPlayerTypeForPlayer(state, playerId, streamInfo);

          state.byPlayerId[playerId] = {
            state: {
              streamInfo,
              playerType,
              deviceOffline: undefined,
              restartRequired: false,
              seekRequired: undefined,
              inGap: undefined,
              playlist: undefined,
              nextPlaylist: undefined,
              liveplaylist: undefined,
              corrections: 0,
              restarts: 0,
              waitingCount: 0,
              lastStateChange: new HighResTimestamp(),
              loadingStart: undefined,
              playingStart: undefined,
              pausingStart: undefined,
              totalPausedTime: 0,
              errors: [],
            },
            status: undefined,
          };
        }
      }

      // Remove any players we used to have that are now missing.
      const extraPlayerIds = new Set(Object.keys(state.byPlayerId));
      for (const { playerId } of cameras) {
        extraPlayerIds.delete(playerId);
      }

      if (extraPlayerIds.size) {
        // We have some extra players.  Delete extra state...
        for (const playerId of extraPlayerIds) {
          delete state.byPlayerId[playerId];
          delete this.playerLogs[playerId];
        }
      }

      this._updatePlayers(state);
    });
  }

  /** Set the preferred playlist source. */
  updatePlaylistSource(playlistSource: SelectedPlayListSource): void {
    this.playListSource = playlistSource;
    this.state.update((state) => this._updatePlayers(state));
  }

  /**
   * Set the preferred video player for a specific player.
   */
  setPreferredVideoPlayer(
    playerId: string,
    playerType: VideoUtil.VideoPlayerType | undefined,
  ): void {
    if (this.preferredVideoPlayerOverride[playerId] !== playerType) {
      this.preferredVideoPlayerOverride[playerId] = playerType;
      // If the player is on the page
      if (this.state.current.byPlayerId[playerId]) {
        this.state.update((state) => {
          this._updatePlayer(state, playerId);
        });
      }
    }
  }

  /**
   * Set the fallback player type by playerId to be used when player encounters an error
   */
  setPlayerTypeFallback({
    playerId,
    playerType,
    error,
  }: {
    playerId: string;
    playerType: VideoUtil.VideoPlayerType | undefined;
    error?: unknown;
  }): void {
    this.playerTypeFallback[playerId] = playerType;
    // If the player is on the page
    if (this.state.current.byPlayerId[playerId]) {
      this.state.update((state) => {
        this._updatePlayer(state, playerId);

        /** skip analytics */
        if (!playerType) {
          return;
        }

        const analyticPropeties = {
          ...this._getAnalyticsTrackingInfo(state, playerId),
          error: getErrorMessage(error),
          fallbackPlayerType: playerType,
        };

        if (this.videoPlayerPlatform === 'WebVideoWall') {
          this.videoWallAnalyticsCache?.addEventByCameraId(
            'playerTypeFallback',
            analyticPropeties.cameraId,
            analyticPropeties,
          );
        } else {
          videoAnalytics.playerTypeFallback(analyticPropeties);
        }
      });
    }
  }

  /**
   * Retrieve the preferred video player for a specific playerId.
   */
  public getPreferredVideoPlayer(playerId: string): VideoUtil.VideoPlayerType | undefined {
    return this.preferredVideoPlayerOverride[playerId];
  }

  /** Forcefully reload a player. */
  restartPlayer(playerId: string, reason: string) {
    this.getPlayerLog(playerId).warn(`${PC} Restarting player: ${reason}`);

    this._updatePlayerState(playerId, (state) => {
      state.restartRequired = true;
      state.restarts++;
    });
  }

  /** Set the current playback speed. */
  setPlaybackRate = (playbackRate: number): void => {
    this.state.update((state) => {
      state.playbackRate = playbackRate;
    });
  };

  incrementRewindPlaybackRate = () => {
    const { playbackRate: currentPlaybackRate } = this.state.current;
    const maxPlaybackRate = VideoUtil.getMaxPlaybackRate(this.playingCameras);
    let newPlaybackRate: number;
    const incrementedPlaybackRate = currentPlaybackRate * 2;
    // Initialize playback rate to -1
    if (currentPlaybackRate > -1) {
      newPlaybackRate = -1;
      // Reset playback rate back to 1 if it exceeds the limit.
    } else if (-incrementedPlaybackRate > maxPlaybackRate) {
      newPlaybackRate = 1;
      // Otherwise increment playback rate by a factor of 2
    } else {
      newPlaybackRate = incrementedPlaybackRate;
    }

    // Play if we're currently paused
    if (this.isPaused() && newPlaybackRate === -1) {
      this.togglePaused(false);
    }
    // Pause video if we exceed the limit
    else if (!this.isPaused() && newPlaybackRate === 1) {
      this.togglePaused(true);
    }

    this.setPlaybackRate(newPlaybackRate);
    return newPlaybackRate;
  };

  incrementFastForwardPlaybackRate = () => {
    let newPlaybackRate: number;
    const { playbackRate: currentPlaybackRate } = this.state.current;
    const maxPlaybackRate = VideoUtil.getMaxPlaybackRate(this.playingCameras);

    const incrementedPlaybackRate = currentPlaybackRate * 2;
    // Initialize playback rate to 2 if we're rewinding or playing video normally
    if (currentPlaybackRate <= 1) {
      newPlaybackRate = 2;
    }
    // Reset playback rate back to 1 if it exceeds the limit
    else if (incrementedPlaybackRate > maxPlaybackRate) {
      newPlaybackRate = 1;
    }
    // Otherwise increment playback rate by a factor of 2
    else {
      newPlaybackRate = incrementedPlaybackRate;
    }
    // If video is currently paused, play it
    if (this.isPaused()) {
      this.togglePaused(false);
    }
    this.setPlaybackRate(newPlaybackRate);
    return newPlaybackRate;
  };

  /**
   * Toggle playing HD/SD.
   */
  toggleQuality = (): void => {
    const oldQuality = this.state.current.playingQuality;
    const newQuality = oldQuality === 'hd' ? 'sd' : 'hd';
    this.setQuality(newQuality);
  };

  /**
   * Set playing quality HD/SD/AD.
   *
   * @param qualityOverride Force the quality to be HD, SD, or AD.
   */
  setQuality = (qualityOverride: StreamQuality): void => {
    const oldQuality = this.state.current.playingQuality;
    const newQuality = qualityOverride;

    if (newQuality !== oldQuality) {
      this.tick();

      this.state.update((state) => {
        state.playingQuality = newQuality;
        this._updatePlayers(state);
      });
    }
  };

  /**
   * Toggle the current play/pause state.
   *
   * @param paused If specified, force the state to be paused (true) or playing
   *   (false).  Otherwise, toggle between paused and playing.
   */
  togglePaused = (paused?: boolean, forced?: boolean): void => {
    // User can pause between ticks.  Call `tick()` here so the `desiredTime`
    // will be exactly at the spot where we pause.
    // Exception: Don't tick if the video has been forcefully paused by the controller to prevent an infinite loop
    if (!forced) {
      this.tick();
    }

    this.state.update((state) => {
      const newPaused = paused ?? state.playbackState !== 'paused';

      if (newPaused) {
        this.setPlaybackRate(1);
      }

      state.lastUpdated = Date.now();

      if (newPaused && state.playbackState !== 'paused') {
        if (this._isPlayingLive(state)) {
          // If we try to pause while playing live, seek back to before
          // the live mode barrier.
          this.seekToTime(state.liveModeBarrier - 1000);
        }
        state.playbackState = 'paused';
      } else if (!newPaused && state.playbackState === 'paused') {
        state.playbackState = 'playing';
        if (state.desiredTime >= state.liveModeBarrier && !this.disableLiveModeBarrier) {
          this.playLive();
        }
      }
    });
  };

  /** Called by a player to update it's status. */
  updatePlayerStatus(playerId: string, status: PlayerStatus): void {
    const playingLive = this.isPlayingLive();
    if ((playingLive && status.type === 'Archived') || (!playingLive && status.type === 'Live')) {
      // Ignore update from wrong player type.  This can happen when we toggle
      // from playing live to archived or back.
      this.getPlayerLog(playerId).debug(`${PC} Ignoring update from stale ${status.type} player.`);
      return;
    }

    this.state.update((state) => {
      const playerState = state.byPlayerId[playerId];
      if (playerState) {
        const oldStatus = playerState.status;

        // Preserve the lastUnbufferedSeekDuration from the previous update, if
        // there was one.
        const newStatus = {
          lastUnbufferedSeekDuration: playerState.status?.lastUnbufferedSeekDuration,
          ...castDraft(status),
        };

        const log = this.getPlayerLog(playerId);

        if (newStatus.state !== oldStatus?.state) {
          log.info(`${PC} Player switched state: ${oldStatus?.state} -> ${newStatus.state}`);
          switch (newStatus.state) {
            case 'paused':
              // Only consider the pause time before the video starts playing.
              if (playerState.state.playingStart === undefined) {
                playerState.state.pausingStart = newStatus.timestamp;
              }
              break;
            default:
              if (playerState.state.pausingStart) {
                // Add the time the player was paused to the total paused time. Considers the case where the player is paused multiple times.
                playerState.state.totalPausedTime += newStatus.timestamp.difference(
                  playerState.state.pausingStart,
                ).value;
                playerState.state.pausingStart = undefined;
              }
              break;
          }

          // Record analytics about loading video and load times.
          if (newStatus.state === 'loading') {
            if (this.user) {
              const analyticPropeties = this._getAnalyticsTrackingInfo(state, playerId);
              if (this.videoPlayerPlatform === 'WebVideoWall') {
                this.videoWallAnalyticsCache?.addEventByCameraId(
                  'loadVideo',
                  analyticPropeties.cameraId,
                  analyticPropeties,
                );
              } else {
                videoAnalytics.loadVideo(this._getAnalyticsTrackingInfo(state, playerId));
              }
            }
            playerState.state.loadingStart = newStatus.timestamp;
            // Clear playingStart so we'll measure the load time.
            playerState.state.playingStart = undefined;
          } else if (newStatus.state === 'playing') {
            if (playerState.state.loadingStart && playerState.state.playingStart === undefined) {
              playerState.state.playingStart = newStatus.timestamp;
              const pausedDuringLoadingSec = playerState.state.totalPausedTime / 1000;
              const loadTimeSec =
                playerState.state.playingStart.difference(playerState.state.loadingStart).value /
                1000;
              log.info(`${PC} Video loaded in ${loadTimeSec}s`);
              if (this.user) {
                const analyticPropeties = {
                  ...this._getAnalyticsTrackingInfo(state, playerId),
                  loadTimeSec,
                  errors: JSON.parse(JSON.stringify(playerState.state.errors)),
                  visibilityState: document.visibilityState,
                  pausedDuringLoadingSec,
                  estimatedDownloadSpeedMbps: (navigator as any).connection?.downlink,
                  hardwareConcurrency: navigator.hardwareConcurrency,
                  cpuPressure: this.pressureStateTracker.pressure,
                  candidateType: '',
                };
                if (analyticPropeties.playerType === 'WebRTC' && analyticPropeties.playerInfo) {
                  const regex = /\(.+?\((.+?)\).+?\(.+?\).+?\((.+?)\).+?\(.+?\)/;
                  const [_, local, remote] = regex.exec(analyticPropeties.playerInfo) ?? [];
                  analyticPropeties.candidateType = `${local}/${remote}`;
                }
                if (this.videoPlayerPlatform === 'WebVideoWall') {
                  this.videoWallAnalyticsCache?.addEventByCameraId(
                    'videoLoadTime',
                    analyticPropeties.cameraId,
                    analyticPropeties,
                  );
                } else {
                  videoAnalytics.measureVideoLoadTime(analyticPropeties);
                }
              }
            }
          } else if (newStatus.state === 'error') {
            playerState.state.errors.push(getErrorMessage(newStatus.error));
          }

          playerState.state.lastStateChange = status.timestamp;
        }
        if (newStatus.error !== oldStatus?.error) {
          if (newStatus.error) {
            log.error(`${PC} Player error:`, status.error);
            if (this.user) {
              const analyticPropeties = {
                ...this._getAnalyticsTrackingInfo(state, playerId),
                error: getErrorMessage(newStatus.error),
              };
              if (this.videoPlayerPlatform === 'WebVideoWall') {
                this.videoWallAnalyticsCache?.addEventByCameraId(
                  'playbackError',
                  analyticPropeties.cameraId,
                  analyticPropeties,
                );
              } else {
                videoAnalytics.videoPlaybackError(analyticPropeties);
              }
            }
          }
        }

        // Update the player status.
        playerState.status = newStatus;

        // Clear seekRequired.
        if (newStatus.seekAcknowledged) {
          if (playerState.state.seekRequired === undefined) {
            log.warn(
              `${PC} Unexpected seekAcknowledged for ${playerId}: ${newStatus.seekAcknowledged}`,
            );
          } else if (newStatus.seekAcknowledged === playerState.state.seekRequired.seekTo) {
            playerState.state.seekRequired = undefined;
          }
        }

        if (newStatus.restartAcknowledged) {
          if (playerState.state.restartRequired) {
            playerState.state.restartRequired = false;
            playerState.state.seekRequired = undefined;
          } else {
            log.warn(`${PC} Unexpected restartAcknowledged for ${playerId}`);
          }
        }

        // If the player is switching to "waiting", increase the "waitingCount".
        if (newStatus.state === 'waiting' && oldStatus?.state !== 'waiting') {
          playerState.state.waitingCount++;
        }

        this._updatePlayer(state, playerId);
        this._skipGaps(state);
      }
    });
  }

  /**
   * Helper function to update the state of all players.
   */
  private _updatePlayers(state: Draft<PlaybackControllerState>, forceReloadPlaylist?: boolean) {
    for (const playerId of Object.keys(state.byPlayerId)) {
      this._updatePlayer(state, playerId, forceReloadPlaylist);
    }
  }

  /** If all players are in a gap, this will skip ahead to the end of the closest gap. */
  private _skipGaps(state: Draft<PlaybackControllerState>) {
    // If all players are `inGap` then skip ahead to the end of the next gap.
    if (state.skipGaps) {
      const skippable = findSkippableGap(state);
      if (skippable) {
        if (state.playbackRate < 0) {
          state.desiredTime = skippable.start;
        } else {
          state.desiredTime = skippable.end;
        }

        // Find all players that are waiting until the end of the gap, and resume them.
        for (const [playerId, player] of Object.entries(state.byPlayerId)) {
          if (player && player.state.inGap?.end === skippable.end) {
            this.getPlayerLog(playerId).info(`${PC} Skipping gap.`);
            player.state.inGap = undefined;
          }
        }
      }
    }
  }

  private _updatePlayer(
    state: Draft<PlaybackControllerState>,
    playerId: string,
    forceReloadPlaylist?: boolean,
  ) {
    this._updatePlayerStream(state, playerId);

    // Don't need to worry about loading playlists in live mode.
    if (state.playbackState !== 'live') {
      const updatedPlaylist = this._updatePlayerPlaylist(state, playerId, forceReloadPlaylist);
      if (!updatedPlaylist) {
        this._syncPlayer(state, playerId);
      }
    }

    const { byPlayerId } = state;
    const playerState = byPlayerId[playerId];
    if (!playerState) {
      throw new Error(`No such player ${playerId}`);
    }

    // Update the player type.
    playerState.state.playerType = this._getPlayerTypeForPlayer(
      state,
      playerId,
      playerState.state.streamInfo,
    );

    if (playerState.status?.state === 'error') {
      const timeSinceLastStatusChange = new HighResTimestamp().difference(
        playerState.state.lastStateChange,
      ).value;
      if (timeSinceLastStatusChange >= PLAYER_ERROR_CHECKING_HEART_BEAT * 1000) {
        this.restartPlayer(playerId, 'Player stuck in error for too long');
      }
    }
  }

  /**
   * Helper function to update the state of an individual player.
   */
  private _updatePlayerState(
    playerId: string,
    fn: (
      playerState: Draft<PlayerState>,
      status: Draft<PlayerStatus> | undefined,
      controllerState: Draft<PlaybackControllerState>,
    ) => void,
  ) {
    this.state.update((state) => {
      const playerState = state.byPlayerId[playerId];
      if (playerState) {
        fn(playerState.state, playerState.status, state);
      }
    });
  }

  /**
   * Helper function to load a playlist for the given playerId.
   *
   * This will asynchronously load the player in the background, although the
   * playlist will be updated to `state: 'loading'` immediately and synchronously.
   *
   * @param state - The current state of the playback controller.
   * @param playerId - The player to load.
   * @param playlistType - Should we load the current or next playlist?
   * @param options.short - If true, load a "short" playlist if the cache doesn't
   *   have the full playlist.
   * @param forceStream - If true, force the selected stream and source to
   *   those provided.
   */
  private _loadPlaylist(
    state: Draft<PlaybackControllerState>,
    playerId: string,
    playlistType: 'playlist' | 'nextPlaylist',
    desiredTime: number,
    options: {
      short?: boolean;
      forceStream?: StreamInfo;
    } = {},
  ) {
    const { short = false } = options;
    const { playingQuality } = state;

    const playingCamera = this.playingCamerasByPlayerId[playerId];
    if (!playingCamera) {
      throw new Error(`Missing playingCamera for ${playerId}`);
    }

    const playerState = state.byPlayerId[playerId];
    const mutableStreamInfo = options.forceStream ?? playerState?.state.streamInfo;
    // If `mutableStreamInfo` came from playerState, then it's a mutable proxy
    // from immer - need to turn it into an immutable object, because `_loadPlaylistAsync`
    // will use it outside of this update.
    const streamInfo = mutableStreamInfo ? { ...mutableStreamInfo } : undefined;

    const canLoad = canLoadPlaylist(playerState, playlistType);

    if (!playerState) {
      throw new Error(`No player state for ${playerId}`);
    } else if (!canLoad) {
      // Can't load the playlist right now, we'll try again later...
    } else if (!streamInfo) {
      this.getPlayerLog(playerId).warn(`${PC} No stream available for playback.`);
      const error = new Error('No stream available.');
      playerState.state[playlistType] = {
        state: 'error',
        error: new Error('No stream available.'),
        timestamp: Date.now(),
      };
      if (this.user) {
        videoAnalytics.videoPlaybackError({
          ...this._getAnalyticsTrackingInfo(state, playerId),
          error: error.message,
        });
      }
    } else {
      playerState.state[playlistType] = { state: 'loading' };
      this._loadPlaylistAsync(
        playingCamera,
        streamInfo,
        playlistType,
        desiredTime,
        playingQuality,
        short,
        !options.forceStream,
        this.thumbnailLevel,
        this.segmentDurationMs,
      );
    }
  }

  private _loadPlaylistAsync(
    playingCamera: PlayingCamera,
    streamInfo: StreamInfo,
    playlistType: 'playlist' | 'nextPlaylist',
    desiredTime: number,
    playingQuality: StreamQuality,
    short: boolean,
    shouldCheckDeviceOffline: boolean,
    thumbnailLevel?: ThumbnailLevel,
    segmentDurationMs?: number,
  ) {
    const accessibleAddress = getAccessibleAddress(
      playingCamera.camera,
      this.localLocations[playingCamera.locationId],
    );

    // TODO: Abort this load if we remove the player?
    // Or assign load a unique ID so we can ignore stale loads?
    Promise.resolve()
      .then(async () => {
        const { isDeviceOnline, segmentData, loadedShort, fromCache } = await loadSegments({
          user: this.user,
          desiredTime,
          checkDeviceOnline: shouldCheckDeviceOffline ? this._checkDeviceOnline : undefined,
          domain: this.getLoadedVideoDomain(desiredTime, this.timezone),
          playingCamera,
          playlistCache: this.playlistCache,
          playlistSource: this.playListSource,
          streamInfo,
          short,
          vtt: this.vtt,
          playingQuality,
          accessibleAddress,
          thumbnailLevel,
          segmentDurationMs,
        });

        if (short && loadedShort && segmentData.segments.length === 0) {
          this.getPlayerLog(playingCamera.playerId).warn(
            `${PC} Loaded short playlist ${segmentsToString(segmentData)}` +
              `${fromCache ? ' from cache' : ''}, but it was empty`,
          );

          // Tried to load short data, but the playlist was empty.  Jump to the
          // full playlist instead of using this playlist.
          this._loadPlaylistAsync(
            playingCamera,
            streamInfo,
            playlistType,
            desiredTime,
            playingQuality,
            false,
            shouldCheckDeviceOffline,
            thumbnailLevel,
            segmentDurationMs,
          );
        } else {
          this.getPlayerLog(playingCamera.playerId).info(
            `${PC} Loaded ${short ? 'short ' : ''}` +
              `playlist ${segmentsToString(segmentData)}` +
              `${fromCache ? ' from cache' : ''}`,
          );

          this.state.update((state) => {
            const playerState = state.byPlayerId[playingCamera.playerId]?.state;
            if (!playerState) {
              return;
            }

            playerState[playlistType] = {
              state: 'loaded',
              playingQuality,
              short: loadedShort,
              ...castDraft(segmentData),
            };

            if (playlistType === 'playlist') {
              playerState.seekRequired = undefined;
              playerState.inGap = checkInGap(state.playbackState, playerState, desiredTime);
            }

            if (isDeviceOnline !== undefined) {
              playerState.deviceOffline = !isDeviceOnline;
            }
          });
        }
      })
      .catch((err) => {
        this.getPlayerLog(playingCamera.playerId).error(
          `${PC} Error loading playlist for stream ${streamInfo.streamId} from ${streamInfo.source}:\n`,
          err,
        );

        this._updatePlayerState(playingCamera.playerId, (playerState, _status, state) => {
          playerState[playlistType] = { state: 'error', error: err, timestamp: Date.now() };
          if (this.user) {
            videoAnalytics.videoPlaybackError({
              ...this._getAnalyticsTrackingInfo(state, playingCamera.playerId),
              error: err.message,
            });
          }
        });
      });
  }

  /**
   * Updates the selected stream for a player.
   */
  private _updatePlayerStream(state: Draft<PlaybackControllerState>, playerId: string) {
    const playerState = state.byPlayerId[playerId];

    const streamInfo = this._getStreamInfoForPlayer(state, playerId);
    const currentStreamInfo = playerState?.state.streamInfo;
    if (
      (currentStreamInfo &&
        (currentStreamInfo.streamId !== streamInfo?.streamId ||
          currentStreamInfo.source !== streamInfo?.source ||
          currentStreamInfo.quality !== streamInfo?.quality)) ||
      (playerState && currentStreamInfo === undefined && streamInfo !== undefined)
    ) {
      this.getPlayerLog(playerId).info(
        `${PC} Updating stream: ${streamInfo?.source} ${streamInfo?.streamId}`,
      );
      this.selectedStreamByPlayerId[playerId] = this._getStreamForPlayer(state, playerId);
      playerState.state.streamInfo = streamInfo;
    }
  }

  private _getStreamForPlayer(
    state: PlaybackControllerState,
    playerId: string,
  ): SelectedStream | undefined {
    const isLive = this.state.current.playbackState === 'live';
    const playingCamera = this.playingCamerasByPlayerId[playerId];
    // fixed playlist cameras only have one stream
    if (!playingCamera) {
      return undefined;
    }

    const selectedStream = VideoUtil.selectStream({
      location: playingCamera.device,
      streams: playingCamera.camera.streams,
      targetStartTime: state.desiredTime,
      quality: state.playingQuality,
      selectedPlayListSource: this.playListSource,
      isDeviceOffline: state.byPlayerId[playerId]?.state.deviceOffline,
      thumbnailLevel: this.thumbnailLevel,
    });

    /**
     *  serverless cloudVMS isn't setup to support the live use case
     *  live in cloudVMS works more like latest in the cloud (can be from a while back)
     */
    if (selectedStream?.source === 'cloud' && isLive) {
      return undefined;
    }

    return selectedStream;
  }

  private _getStreamInfoForPlayer(
    state: PlaybackControllerState,
    playerId: string,
  ): StreamInfo | undefined {
    const selectedStream = this._getStreamForPlayer(state, playerId);

    if (selectedStream) {
      return {
        streamId: selectedStream.stream.id,
        source: selectedStream.source,
        quality: selectedStream.quality,
      };
    }
    return undefined;
  }

  private _getPlayerTypeForPlayer(
    state: PlaybackControllerState,
    playerId: string,
    streamInfo: StreamInfo | undefined,
  ): VideoUtil.VideoPlayerType | undefined {
    const playingCamera = this.playingCamerasByPlayerId[playerId];

    const stream =
      streamInfo &&
      playingCamera?.camera.streams.find((stream) => stream.id === streamInfo.streamId);

    const playerOverride =
      this.preferredVideoPlayerOverride[playerId] ?? this.playerTypeFallback[playerId];

    if (!stream) {
      return undefined;
    } else if (playingCamera.camera.videoUrl) {
      if (playingCamera.camera.liveUrl && this._isPlayingLive(state)) {
        return 'WebRTC';
      }
      return 'HLS';
    } else if (this.user) {
      return VideoUtil.selectPlayerType({
        user: this.user,
        location: playingCamera.location,
        locationIsLocal: this.localLocations[playingCamera.locationId] || false,
        camera: playingCamera.camera,
        selectedStream: { stream, source: streamInfo.source, quality: streamInfo.quality },
        preferredVideoPlayer: playerOverride,
        quick: this.quick,
        playingLive: this._isPlayingLive(state),
        numberOfPlayingCameras: Object.keys(this.playingCamerasByPlayerId).length,
        device: playingCamera.device,
      });
    }
    return undefined;
  }

  /**
   * Helper function which checks to see if a player has the correct playlist
   * loaded, and loads the correct playlist if not.
   *
   * This is called every time we update players, for every player.
   *
   * @returns true if we updated the playlist, false otherwise.
   */
  private _updatePlayerPlaylist(
    state: Draft<PlaybackControllerState>,
    playerId: string,
    forceReloadPlaylist?: boolean,
  ): boolean {
    const { desiredTime, byPlayerId } = state;
    const playerState = byPlayerId[playerId];
    if (!playerState) {
      throw new Error(`No such player ${playerId}`);
    }

    let answer = false;

    const currentPlaylist = playerState.state.playlist;

    let needReload = false;
    const playlist = playerState.state?.playlist;
    if (
      playlist?.state === 'loaded' &&
      (playlist.streamId !== playerState.state.streamInfo?.streamId ||
        playlist.source !== playerState.state.streamInfo?.source ||
        playlist.playingQuality !== playerState.state.streamInfo?.quality ||
        forceReloadPlaylist)
    ) {
      // We've switched streams.
      needReload = true;
    }

    if (needReload) {
      this._loadPlaylist(state, playerId, 'playlist', desiredTime, { short: false });
      answer = true;
    } else if (inLoadedRange(playerState.state.nextPlaylist, desiredTime)) {
      // Switch to the next playlist.
      playerState.state.playlist = playerState.state.nextPlaylist;
      playerState.state.nextPlaylist = undefined;
      playerState.state.inGap = checkInGap(state.playbackState, playerState.state, desiredTime);
      answer = true;
    } else if (!inLoadedRange(playerState.state.playlist, desiredTime)) {
      // Load a new current playlist.
      this._loadPlaylist(state, playerId, 'playlist', desiredTime, {
        short: !this.isThumbnailStream(),
      });
      answer = true;
    } else if (
      currentPlaylist?.state === 'loaded' &&
      playerState.state.nextPlaylist?.state !== 'loaded'
    ) {
      // If we currently have a short playlist loaded, then pre-fetch the
      // full playlist.
      if (currentPlaylist.short) {
        this._loadPlaylist(state, playerId, 'nextPlaylist', desiredTime, {
          short: false,
          forceStream: {
            streamId: currentPlaylist.streamId,
            source: currentPlaylist.source,
            quality: currentPlaylist.playingQuality,
          },
        });
      } else if (
        currentPlaylist?.state === 'loaded' &&
        currentPlaylist.end - desiredTime < PREFETCH_THRESHOLD &&
        Date.now() - desiredTime > PREFETCH_THRESHOLD * 2
      ) {
        // We're getting close to "tomorrow".  Pre-load tomorrow's playlist.
        // If we're close to "now", and also close to the end of the day, there's
        // not much point in trying to preload tomorrow's playlist, since it will
        // be mostly empty.
        this._loadPlaylist(state, playerId, 'nextPlaylist', currentPlaylist.end + 1000, {
          short: false,
          forceStream: {
            streamId: currentPlaylist.streamId,
            source: currentPlaylist.source,
            quality: currentPlaylist.playingQuality,
          },
        });
      }
    }

    return answer;
  }

  private _isPlayingLive(state: PlaybackControllerState) {
    return state.playbackState === 'live';
  }

  isPlayingLive(): boolean {
    return this._isPlayingLive(this.state.current);
  }
  isCloseToLive(): boolean {
    return (
      this.state.current.desiredTime > new Date().getTime() - CLOSE_TO_LIVE_OFFSET &&
      !this.isPlayingLive()
    );
  }

  isPaused() {
    return this.state.current.playbackState === 'paused';
  }

  isRewinding() {
    return this.state.current.playbackRate < 0;
  }

  isFastForwarding() {
    return this.state.current.playbackRate > 1;
  }

  isBuffered(buffers: PlayerStatus['buffered'], time: number) {
    return isBuffered(buffers, time);
  }

  /**
   * Returns true if one or more players are currently playing video.
   */
  anyPlayersPlaying() {
    return Object.values(this.state.current.byPlayerId).some(
      (playerState) => playerState?.status?.state === 'playing',
    );
  }

  /**
   * Returns a CircularLog for the given player.
   *
   * Note that the returned object is *not* immutable.
   * You can get logs to be printed to the console as well by running the following
   * in the console:
   *
   *     localStorage['HLS_DEBUG'] = true
   *
   * Also note that some logs end up going to the wrong logger (see https://github.com/video-dev/hls.js/issues/4286).
   * And, some logs from the demuxer end up going to stdout because the demuxer
   * is running in a webworker (see https://github.com/video-dev/hls.js/issues/2701).
   */
  getPlayerLog(playerId: string): CircularLog {
    this.playerLogs[playerId] =
      this.playerLogs[playerId] ||
      new CircularLog({
        consoleLogKey: HLS_DEBUG,
        maxLogEntries: DEFAULT_MAX_LOG_ENTRIES,
      });
    return this.playerLogs[playerId];
  }

  getSelectedStream(playerId: string): SelectedStream | undefined {
    return this.selectedStreamByPlayerId[playerId];
  }

  /** Returns true if the given playerId is currently out-of-sync. */
  private _isOutOfSync(state: Draft<PlaybackControllerState>, playerId: string): boolean {
    const playerState = state.byPlayerId[playerId];
    const { desiredTime, playbackRate } = state;

    if (
      !playerState ||
      !playerState.state.playlist ||
      !playerState.status ||
      playerState.status.state === 'loading' ||
      playerState.status.state === 'error'
    ) {
      // Nothing to do for this player.
      return false;
    }

    if (playerState.state.inGap) {
      // Player has no video for the desired time, and is waiting...
      return false;
    }

    if (playerState.status.state === 'ended') {
      // Ended player either has no video left to play in which case
      // we need up update `inGap`, or needs to be seeked back to
      // somewhere it has video.
      return true;
    }

    let wallclockTime = playerState.status?.wallclockTime;
    if (wallclockTime) {
      // If it's been 500ms since we last got an update from the player, then
      // the player is (probably) 500ms ahead of where it used to be.  Note that
      // we don't want to apply this while paused, because we don't get updates
      // from the player while paused.
      const elapsed =
        state.playbackState === 'paused'
          ? 0
          : Math.max(0, new HighResTimestamp().difference(playerState.status.timestamp).value);
      wallclockTime = wallclockTime + elapsed * playbackRate;
    }

    // If the player is already seeking, use the new seek time as the time to
    // check against.  Otherwise we end up in a cycle where a player gets out
    // of sync, we seek it to a new time, while it's loading that time it gets
    // further behind, and then by the time it's done we're out of sync again.
    const playerTime = playerState.state.seekRequired?.seekTo ?? wallclockTime;

    if (playerTime !== undefined) {
      const syncTolerance =
        playbackRate > 1 ? OUT_OF_SYNC_TOLERANCE * playbackRate : OUT_OF_SYNC_TOLERANCE;
      return Math.abs(playerTime - desiredTime) >= syncTolerance;
    }

    // We don't know if this player is out-of-sync or not.
    return false;
  }

  /**
   * Helper function which makes sure the player is playing the time we want
   * it to play.
   *
   * This is called every time we update players, for every player.
   */
  private _syncPlayer(state: Draft<PlaybackControllerState>, playerId: string) {
    const { byPlayerId, playbackRate, desiredTime } = state;
    const playerState = byPlayerId[playerId];
    const playlist = playerState?.state.playlist;
    const isPaused = state.playbackState === 'paused';

    if (!playerState || playlist?.state !== 'loaded' || playerState.status?.state === 'error') {
      // Can't sync a player if we don't even know what it's playing.
      return;
    }

    if (
      playerState.state.seekRequired &&
      Date.now() - playerState.state.seekRequired.requestedAt > MAX_SEEK_TIME
    ) {
      // This player has been seeking for a really long time.  Murder it.
      this.restartPlayer(playerId, 'Player has been seeking for a really long time');
    } else if (this._isOutOfSync(state, playerId)) {
      const seekToTime = getAdjustedSeekTime(
        desiredTime,
        playerState.status,
        playbackRate,
        isPaused,
      );

      // Find the segment we're supposed to be playing.
      const { segments } = playlist;
      const lastSegment = segments.length > 0 ? segments[segments.length - 1] : undefined;

      const isEnded =
        !lastSegment ||
        (playerState.status?.state === 'ended' &&
          lastSegment.end - seekToTime < ENDED_TOLERANCE_MS);

      if (isEnded && !this.isCloseToLive()) {
        this.getPlayerLog(playerId).info(`${PC} Player has ended.`);
        playerState.state.inGap = {
          start: lastSegment?.end ?? playlist.start,
          end: playlist.end,
          indefinite: true,
        };
      } else {
        const inGap = checkInGap(state.playbackState, playerState.state, seekToTime);
        if (inGap) {
          playerState.state.inGap = inGap;
          this.getPlayerLog(playerId).info(
            `${PC} Entering gap: ` +
              `${new Date(inGap.start).toISOString()}-${new Date(inGap.end).toISOString()}.`,
          );
        } else {
          // We are not in a gap, so the player has just drifted from the desired play time.
          if (
            !playerState.state.lastCorrectionTimestamp ||
            Date.now() - playerState.state.lastCorrectionTimestamp > MIN_TIME_BETWEEN_CORRECTIONS
          ) {
            this.getPlayerLog(playerId).warn(`${PC} Correcting playback time.`);
            playerState.state.seekRequired = { seekTo: seekToTime, requestedAt: Date.now() };
            playerState.state.corrections++;
            playerState.state.lastCorrectionTimestamp = Date.now();
          }
        }
      }
    } else if (
      !playerState.state.seekRequired &&
      playerState.state.inGap &&
      (desiredTime >= playerState.state.inGap.end || desiredTime <= playerState.state.inGap.start)
    ) {
      // This player was in a gap, but now we've seeked past the gap.
      this.getPlayerLog(playerId).warn(`${PC} Exited gap.`);
      this._updatePlayerState(playerId, (playerState) => {
        playerState.seekRequired = { seekTo: desiredTime, requestedAt: Date.now() };
        playerState.inGap = undefined;
      });
    }
  }

  private _getAnalyticsTrackingInfo(
    state: PlaybackControllerState,
    playerId: string,
  ): videoAnalytics.PlayerTrackingInfo {
    if (!this.user) {
      throw new Error('_getAnalyticsTrackingInfo can only be used for authenticated sessions');
    }

    return videoAnalytics.getMixpanelVideoTrackingInfo({
      appDate: state.desiredTime,
      cameraCount: this.playingCameras.length,
      playerType: state.byPlayerId[playerId]?.state.playerType,
      playerInfo: state.byPlayerId[playerId]?.status?.playerInfo,
      playingCamera: this.playingCamerasByPlayerId[playerId],
      playingLive: this._isPlayingLive(state),
      selectedPlaylistSource: this.playListSource,
      selectedStream: this.getSelectedStream(playerId),
      user: this.user,
      platform: this.videoPlayerPlatform,
      locationIsLocal:
        this.localLocations[this.playingCamerasByPlayerId[playerId].camera.deviceId] ?? false,
    });
  }

  public isThumbnailStream(): boolean {
    return this.thumbnailLevel === 1;
  }

  updateLivePlaylist(playerId: string, livePlaylist: ParsedPlaylist) {
    this.state.update((state) => {
      const playerState = state.byPlayerId[playerId];
      if (playerState && playerState.state.playlist?.state === 'loaded') {
        playerState.state.liveplaylist = {
          ...playerState.state.playlist,
          ...castDraft(livePlaylist),
        };
        this._syncPlayer(state, playerId);
      }
    });
  }
}

/**
 * Returns true if all players are currently playing and not in a gap.
 */
function allPlayersNotInGap(state: PlaybackControllerState) {
  return Object.values(state.byPlayerId).every(
    (playerState) =>
      playerState &&
      playerState.state.seekRequired === undefined &&
      playerState.status?.state === 'playing' &&
      playerState.state.inGap === undefined,
  );
}

/**
 * If all players in a gap, and the gap can be skipped, this will return the
 * `{ start, end }` we are skipping.  Note that the start and end may come from
 * different players.
 */
function findSkippableGap(state: PlaybackControllerState): PlaybackGap | undefined {
  const gaps: PlaybackGap[] = [];

  for (const playerState of Object.values(state.byPlayerId)) {
    if (!playerState?.state.inGap || playerState.state.inGap.indefinite) {
      return undefined;
    }
    gaps.push(playerState?.state.inGap);
  }

  if (gaps.length === 0) {
    return undefined;
  }

  // Figure out the duration we are skipping.
  const lastGapStart = Math.max(...gaps.map((gap) => gap.start));
  const nextGapEnd = Math.min(...gaps.map((gap) => gap.end));
  return { start: lastGapStart, end: nextGapEnd };
}

/**
 * Returns true if the specified wall clock time is within the range of the
 * loaded playlist.
 */
function inLoadedRange(segmentData: Immutable<PlayerSegments> | undefined, wallClockTime: number) {
  return (
    !!segmentData &&
    segmentData.state === 'loaded' &&
    wallClockTime >= segmentData.start &&
    wallClockTime <= segmentData.end
  );
}

function getRecordingParameters(params: {
  user?: UserSession;
  quality: StreamQuality;
  playingCamera: PlayingCamera;
  start: number;
  end: number;
  selectedPlayListSource: SelectedPlayListSource;
  streamInfo: StreamInfo;
  vtt?: boolean;
  accessibleAddress: string | undefined;
  thumbnailLevel?: ThumbnailLevel;
}) {
  const {
    user,
    quality,
    playingCamera,
    start,
    end,
    selectedPlayListSource,
    streamInfo,
    vtt,
    accessibleAddress,
    thumbnailLevel,
  } = params;

  return {
    user,
    quality,
    location: {
      ...playingCamera.location,
      accessibleAddress,
    },
    vmsCameraId: playingCamera.vmsCameraId,
    streamId: streamInfo.streamId,
    source: streamInfo.source,
    selectedPlayListSource,
    start,
    end,
    vtt,
    thumbnailLevel,
  };
}

/**
 * Returns false if a playlist can't be loaded (because it's already loading, or because it errored recently).
 */
function canLoadPlaylist(
  playerState: Immutable<{ state: PlayerState; status?: PlayerStatus }> | undefined,
  playlistType: 'playlist' | 'nextPlaylist',
): boolean {
  if (!playerState) {
    // We don't know about this player.
    return false;
  }

  const playlist = playerState.state[playlistType];
  if (playlist?.state === 'loading') {
    // Already loading this playlist...
    return false;
  } else if (
    playlist?.state === 'error' &&
    Date.now() - playlist.timestamp < MIN_TIME_BETWEEN_PLAYLIST_RELOADS
  ) {
    // Too soon to load this playlist again...
    return false;
  }

  return true;
}

/**
 * Return `videoUrl` with the `?stream=` query param replaced by `streamId`.
 * Used for VerifEye3 (fixedPlaylistSegmentsUrlProps)
 */
function withStreamId(videoUrl: string, streamId: string): string {
  try {
    const url = new URL(videoUrl);
    url.searchParams.set('stream', streamId);
    return url.toString();
  } catch {
    return videoUrl;
  }
}

/**
 * Load video segments from the playlist cache.
 */
async function loadSegments(params: {
  user?: UserSession;
  playingQuality: StreamQuality;
  playlistCache: PlaylistCache;
  checkDeviceOnline?: (location: AuthLocation) => Promise<boolean>;
  playingCamera: PlayingCamera;
  desiredTime: number;
  domain: [number, number];
  playlistSource: SelectedPlayListSource;
  streamInfo: StreamInfo;
  short: boolean;
  vtt: boolean;
  accessibleAddress: string | null;
  thumbnailLevel?: ThumbnailLevel;
  segmentDurationMs?: number; // If provided, loads a specific duration of video starting from desiredTime
}): Promise<{
  isDeviceOnline: boolean | undefined;
  segmentData: VideoUtil.SegmentData;
  loadedShort: boolean;
  fromCache: boolean;
}> {
  const {
    user,
    playingQuality,
    playlistCache,
    checkDeviceOnline,
    playingCamera,
    desiredTime,
    domain,
    playlistSource,
    streamInfo,
    short,
    vtt,
    accessibleAddress,
    thumbnailLevel,
    segmentDurationMs,
  } = params;

  const [start, end] = domain;

  // If we're loading a playlist for "today", set the end to be the current time.
  // First, beacuse then when we go past that, we'll trigger reloading the playlist.
  // Second, if we're playing close to the live barrier, we'll send different
  // query parameters to the server, and avoid the browser cache.
  const fullRecordingParams: SetOptional<VideoUtil.RecordingUrlParams, 'user'> =
    getRecordingParameters({
      user,
      quality: playingQuality,
      playingCamera,
      start,
      end: Math.min(end, Date.now()),
      selectedPlayListSource: playlistSource,
      streamInfo,
      vtt,
      accessibleAddress: accessibleAddress ?? undefined,
      thumbnailLevel,
    });

  // Note: desiredTime has different meanings depending on segmentDurationMs:
  // - When segmentDurationMs is provided: we are asking for a specific amount of segment data. i.e grab the first clip of an vector embedded search query
  // - When segmentDurationMs is NOT provided: desiredTime is the center time, for optimized fwd/rwd playback
  const desiredStartTime = segmentDurationMs
    ? desiredTime
    : desiredTime - (SHORT_RANGE_SPAN_MS - 1);

  const desiredEndTime = segmentDurationMs
    ? desiredTime + segmentDurationMs
    : desiredTime + SHORT_RANGE_SPAN_MS;

  const shortRecordingParams = {
    ...fullRecordingParams,
    start: Math.max(start, desiredStartTime),
    end: Math.min(end, Date.now(), desiredEndTime),
  };

  const fixedPlaylistSegmentsUrlProps = playingCamera.camera.videoUrl
    ? { url: withStreamId(playingCamera.camera.videoUrl, streamInfo.streamId), vtt }
    : undefined;

  // Always try to load the full segments from the cache first...
  const cachedSegments = await loadCachedSegments(
    playlistCache,
    fullRecordingParams,
    fixedPlaylistSegmentsUrlProps,
  );

  if (cachedSegments) {
    return {
      fromCache: true,
      isDeviceOnline: undefined,
      segmentData: cachedSegments.segmentData,
      loadedShort: false,
    };
  } else if (fixedPlaylistSegmentsUrlProps) {
    const segments = await VideoUtil.getFixedRecordingSegmentsFromM3U8({
      recordingParams: segmentDurationMs ? shortRecordingParams : fullRecordingParams,
      playlistCache,
      fixedPlaylistSegmentsUrlProps,
    });

    return { fromCache: false, ...segments, loadedShort: false };
  }
  if (!fullRecordingParams.user) {
    throw new Error(
      '#loadSegments : playbackController requires user in constructor for authenticated playlist requests',
    );
  }
  // Load video from the cache or from the device/cloud.
  const segments = await VideoUtil.getRecordingSegmentsFromM3U8({
    recordingParams: (short || segmentDurationMs
      ? shortRecordingParams
      : fullRecordingParams) as VideoUtil.RecordingUrlParams,
    playlistCache,
    checkDeviceOnline,
  });
  return { fromCache: false, ...segments, loadedShort: short };
}

/**
 * loadCachedSegments will load segments from the playlist cache, but only if those
 * segments are already in the cache.
 */
async function loadCachedSegments(
  playlistCache: PlaylistCache,
  recordingParams: SetOptional<VideoUtil.RecordingUrlParams, 'user'>,
  fixedPlaylistSegmentsUrlProps?: VideoUrlProps,
): Promise<{ segmentData: VideoUtil.SegmentData } | undefined> {
  // Check to see if the full segments are in the cache.
  let fullSegmentUrl: Immutable<VideoUrlProps> | undefined = undefined;

  if (!fixedPlaylistSegmentsUrlProps && !recordingParams.user) {
    return undefined;
  }

  if (!fixedPlaylistSegmentsUrlProps && recordingParams.user) {
    fullSegmentUrl = VideoUtil.generateRecordingURL(
      recordingParams as VideoUtil.RecordingUrlParams,
    );
  }

  if (fixedPlaylistSegmentsUrlProps) {
    fullSegmentUrl = fixedPlaylistSegmentsUrlProps;
  }

  const dataIsInCache = fullSegmentUrl?.url && playlistCache.hasLoadedPlaylist(fullSegmentUrl.url);

  if (fullSegmentUrl && dataIsInCache) {
    return playlistCache.loadPlaylist(fullSegmentUrl).then((result) => {
      const segmentData: VideoUtil.SegmentData = {
        id: VideoUtil.getSegmentsId(recordingParams),
        streamId: recordingParams.streamId,
        streamUrlProps: fullSegmentUrl as Immutable<VideoUrlProps>,
        source: recordingParams.source,
        start: recordingParams.start,
        end: recordingParams.end,
        ...result.playlist,
      };

      return { segmentData };
    });
  }
  // Not in the cache
  return undefined;
}

/**
 * Returns true if the specified wall clock time is within one of the provided
 * buffers.
 */
function isBuffered(buffers: PlayerStatus['buffered'], time: number): boolean {
  return buffers?.some((buffer) => time >= buffer.start && time <= buffer.end) || false;
}

/**
 * If we're seeking to a time the video player doesn't have buffered, then
 * adjust the seek time by the lastUnbufferedSeekDuration, otherwise if
 * the seek takes a long time we might en up out of sync again by the time
 * the seek is finished.
 */
function getAdjustedSeekTime(
  desiredTime: number,
  playerStatus: PlayerStatus | undefined,
  playbackRate: number,
  isPaused: boolean,
) {
  let seekToTime = desiredTime;
  if (
    playerStatus &&
    playerStatus.lastUnbufferedSeekDuration !== undefined &&
    !isPaused &&
    !isBuffered(playerStatus.buffered, seekToTime)
  ) {
    seekToTime += playerStatus.lastUnbufferedSeekDuration * playbackRate;
  }
  return seekToTime;
}

/**
 * Returns the loaded playlist from a playerState, or undefined if the playlist is not loaded.
 */
export function getLoadedPlaylist(
  playerState: PlayerState | undefined,
): LoadedSegments | undefined {
  return playerState?.playlist?.state === 'loaded' ? playerState.playlist : undefined;
}
export function getLoadedLivePlaylist(
  playerState: PlayerState | undefined,
): LoadedSegments | undefined {
  return playerState?.liveplaylist?.state === 'loaded' ? playerState.liveplaylist : undefined;
}
export function mergeWithLivePlaylist(
  playerState: PlayerState | undefined,
): LoadedSegments | undefined {
  const loadedPlaylist = getLoadedPlaylist(playerState);
  const loadedLivePlaylist = getLoadedLivePlaylist(playerState);

  if (!loadedLivePlaylist) {
    return loadedPlaylist;
  }
  if (loadedPlaylist) {
    return {
      ...loadedPlaylist,
      segments: loadedPlaylist.segments.concat(loadedLivePlaylist?.segments || []),
    };
  }
}

/**
 * If the loaded playlist is empty, or the current desired time is in a gap,
 * return a `PlaybackGap` object.
 */
export function checkInGap(
  playbackState: PlaybackState,
  playerState: PlayerState,
  time: number,
): PlaybackGap | undefined {
  const playlist = playerState.playlist;

  let result: PlaybackGap | undefined;

  const isCloseToLive = time > new Date().getTime() - CLOSE_TO_LIVE_OFFSET;
  if (playbackState === 'live' || isCloseToLive) {
    // We should never set inGap when playing live or when close to playing live.
    return undefined;
  } else if (!playlist || playlist.state !== 'loaded') {
    result = undefined;
  } else if (playlist.segments.length === 0) {
    result = {
      start: playlist.start,
      end: playlist.end,
      indefinite: playlist.short,
    };
  } else {
    const { segments } = playlist;

    const currentSegmentIndex = findClosestSegmentIndex(segments, time);
    const currentSegment = currentSegmentIndex !== -1 ? segments[currentSegmentIndex] : undefined;
    const nextSegment =
      currentSegmentIndex + 1 < segments.length ? segments[currentSegmentIndex + 1] : undefined;
    const lastSegment = segments[segments.length - 1];

    if (isInSegment(currentSegment, time)) {
      result = undefined;
    } else if (nextSegment) {
      // We're in a gap between segments, or we're before the first segment.
      // Pause the player until we reach the next segment.
      result = {
        start: currentSegment?.end ?? playlist.start,
        end: nextSegment.start,
      };
    } else {
      // We're past the end of the playlist.  This means there's no video
      // available until (at least) the end of the current playlist.
      result = {
        start: lastSegment?.end ?? playlist.start,
        end: playlist.end,
        indefinite: true,
      };
    }
  }

  // Do not return a gap if the gap is smaller than the max allowed segment gap.
  if (result && result.end - result.start < MAX_ALLOWED_SEGMENT_GAP) {
    return;
  }

  return result;
}

/**
 * Generate a debug description for a SegmentData object.
 */
function segmentsToString(segments: VideoUtil.SegmentData) {
  return `[${new Date(segments.start).toISOString()},${new Date(segments.end).toISOString()}] (${
    segments.segments.length
  } segments) from ${segments.source}`;
}
